import { useTranslate } from "@tolgee/react";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { KrwSubtext } from "#/components/krw-subtext";
import { Button } from "#/components/ui/button";
import { Checkbox } from "#/components/ui/checkbox";
import { Dialog, DialogTrigger } from "#/components/ui/dialog";
import { Input } from "#/components/ui/input";
import { normalizePerpOrderPrice } from "#/features/trade/lib/order-format";
import { absSizeStr } from "#/features/trade/lib/trade-constants";
import type { OpenOrder } from "#/hooks/use-hyperliquid-account";
import {
	formatPrice,
	formatSignedUsd,
	formatSize,
	toNumber,
} from "#/lib/formatters";
import { TradeDialogContent } from "./trade-dialog-content.tsx";
import type { PositionRowItem } from "./types/positions.types.ts";

type PositionTpslCellProps = {
	position: PositionRowItem;
	tpOrder: OpenOrder | undefined;
	slOrder: OpenOrder | undefined;
	isLong: boolean;
	szDecimals: number;
	onSetTpsl: (params: {
		coin: string;
		isLong: boolean;
		tp: string | null;
		sl: string | null;
		size: string;
		markPrice: number;
		useLimitPrice: boolean;
	}) => void;
	onCancelOrder: (coin: string, oid: number) => void;
};

function calcGainPercent(
	entryPx: number,
	targetPx: number,
	isLong: boolean,
): string {
	if (!entryPx || !targetPx) return "";
	const pct = isLong
		? ((targetPx - entryPx) / entryPx) * 100
		: ((entryPx - targetPx) / entryPx) * 100;
	return pct.toFixed(2);
}

function calcPriceFromPercent(
	entryPx: number,
	percent: number,
	isLong: boolean,
	isTP: boolean,
	szDecimals: number,
): string {
	if (!entryPx || !percent) return "";
	// TP for Long / SL for Short: entry * (1 + pct/100)
	// SL for Long / TP for Short: entry * (1 - pct/100)
	const sign = (isLong && isTP) || (!isLong && !isTP) ? 1 : -1;
	const price = entryPx * (1 + (sign * percent) / 100);
	return normalizePerpOrderPrice(price, szDecimals);
}

// Fees/funding excluded — realized PnL is entry vs trigger price.
export function calcExpectedPnl(
	entryPx: number,
	targetPx: number,
	size: number,
	isLong: boolean,
): number {
	if (!entryPx || !targetPx || !size) return 0;
	return (targetPx - entryPx) * size * (isLong ? 1 : -1);
}

export function PositionTpslCell({
	position,
	tpOrder,
	slOrder,
	isLong,
	szDecimals,
	onSetTpsl,
	onCancelOrder,
}: PositionTpslCellProps) {
	const { t } = useTranslate();
	const [open, setOpen] = useState(false);
	const [tpPrice, setTpPrice] = useState("");
	const [slPrice, setSlPrice] = useState("");
	const [tpGain, setTpGain] = useState("");
	const [slLoss, setSlLoss] = useState("");
	const [configureAmount, setConfigureAmount] = useState(false);
	const [customSize, setCustomSize] = useState("");
	const [useLimitPrice, setUseLimitPrice] = useState(false);

	const tpDisplay = tpOrder ? formatPrice(tpOrder.triggerPx) : "--";
	const slDisplay = slOrder ? formatPrice(slOrder.triggerPx) : "--";
	const absSize = absSizeStr(position.szi);
	const entryPx = toNumber(position.entryPx);
	const markPrice = toNumber(position.markPx);
	// Same effective-size rule as handleSubmit
	const effectiveSize =
		configureAmount && customSize.trim()
			? toNumber(customSize)
			: Math.abs(toNumber(position.szi));
	const tpPnl = calcExpectedPnl(
		entryPx,
		toNumber(tpPrice),
		effectiveSize,
		isLong,
	);
	const slPnl = calcExpectedPnl(
		entryPx,
		toNumber(slPrice),
		effectiveSize,
		isLong,
	);

	function handleOpenChange(nextOpen: boolean) {
		if (nextOpen) {
			const tp = tpOrder ? tpOrder.triggerPx : "";
			const sl = slOrder ? slOrder.triggerPx : "";
			setTpPrice(tp);
			setSlPrice(sl);
			setTpGain(tp ? calcGainPercent(entryPx, toNumber(tp), isLong) : "");
			setSlLoss(sl ? calcGainPercent(entryPx, toNumber(sl), !isLong) : "");
			setConfigureAmount(false);
			setCustomSize("");
			setUseLimitPrice(false);
		}
		setOpen(nextOpen);
	}

	function handleTpPriceChange(value: string) {
		setTpPrice(value);
		const num = toNumber(value);
		setTpGain(num > 0 ? calcGainPercent(entryPx, num, isLong) : "");
	}

	function handleTpGainChange(value: string) {
		setTpGain(value);
		const pct = toNumber(value);
		if (pct > 0) {
			setTpPrice(calcPriceFromPercent(entryPx, pct, isLong, true, szDecimals));
		} else {
			setTpPrice("");
		}
	}

	function handleSlPriceChange(value: string) {
		setSlPrice(value);
		const num = toNumber(value);
		setSlLoss(num > 0 ? calcGainPercent(entryPx, num, !isLong) : "");
	}

	function handleSlLossChange(value: string) {
		setSlLoss(value);
		const pct = toNumber(value);
		if (pct > 0) {
			setSlPrice(calcPriceFromPercent(entryPx, pct, isLong, false, szDecimals));
		} else {
			setSlPrice("");
		}
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();

		// Cancel existing orders first
		if (tpOrder) onCancelOrder(position.coin, tpOrder.oid);
		if (slOrder) onCancelOrder(position.coin, slOrder.oid);

		const tp = tpPrice.trim() || null;
		const sl = slPrice.trim() || null;
		const size =
			configureAmount && customSize.trim() ? customSize.trim() : absSize;

		if (tp || sl) {
			onSetTpsl({
				coin: position.coin,
				isLong,
				tp,
				sl,
				size,
				markPrice,
				useLimitPrice,
			});
		}

		setOpen(false);
	}

	return (
		<div className="flex items-center gap-1.5">
			<span className="text-caption1 text-trade-text-secondary">
				{tpDisplay} / {slDisplay}
			</span>

			<Dialog open={open} onOpenChange={handleOpenChange}>
				<DialogTrigger asChild>
					<button
						type="button"
						className="cursor-pointer text-trade-text-faint hover:text-trade-text-strong"
					>
						<Pencil className="size-3" />
					</button>
				</DialogTrigger>
				<TradeDialogContent
					className="w-[min(440px,calc(100vw-2rem))]"
					title={
						<>
							{t("tpsl.title")} - {position.coin}-USDC -{" "}
							{isLong ? "Long" : "Short"}
						</>
					}
				>
					{/* Position Info Card */}
					<div className="mt-4 rounded-lg border border-trade-border p-3 text-body2">
						<div className="flex justify-between py-1">
							<span className="text-trade-text-faint">
								{t("tpsl.position")}
							</span>
							<span className="text-body2-medium text-trade-green">
								{formatSize(Math.abs(toNumber(position.szi)))} {position.coin}
								-USDC
							</span>
						</div>
						<div className="flex justify-between py-1">
							<span className="text-trade-text-faint">
								{t("tpsl.entry-price")}
							</span>
							<span className="text-trade-text-strong">
								{formatPrice(entryPx)}
							</span>
						</div>
						<div className="flex justify-between py-1">
							<span className="text-trade-text-faint">
								{t("tpsl.mark-price")}
							</span>
							<span className="text-trade-text-strong">
								{formatPrice(markPrice)}
							</span>
						</div>
					</div>

					{/* TP/SL Input Grid */}
					<form onSubmit={(e) => void handleSubmit(e)} className="mt-4">
						<div className="grid grid-cols-2 gap-2">
							{/* TP Price */}
							<div className="relative">
								<span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-label2 text-trade-text-faint">
									{t("tpsl.tp-price")}
								</span>
								<Input
									type="number"
									step="any"
									min="0"
									value={tpPrice}
									onChange={(e) => handleTpPriceChange(e.target.value)}
									placeholder="0.00"
									className="h-10 pl-[72px] text-right text-body2"
								/>
							</div>
							{/* Gain % */}
							<div className="relative">
								<span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-label2 text-trade-text-faint">
									{t("tpsl.gain")}
								</span>
								<Input
									type="number"
									step="any"
									value={tpGain}
									onChange={(e) => handleTpGainChange(e.target.value)}
									placeholder="0"
									className="h-10 pr-8 pl-12 text-right text-body2"
								/>
								<span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-label2 text-trade-text-faint">
									%
								</span>
							</div>

							{/* Expected profit at TP trigger */}
							{toNumber(tpPrice) > 0 && effectiveSize > 0 && (
								<div className="col-span-2 flex justify-between px-1 text-label2">
									<span className="text-trade-text-faint">
										{t("tpsl.expected-profit")}
									</span>
									<span
										className={
											tpPnl >= 0 ? "text-trade-green" : "text-trade-red"
										}
									>
										{formatSignedUsd(tpPnl)}
										<KrwSubtext usd={tpPnl} inheritColor parens inline />
									</span>
								</div>
							)}

							{/* SL Price */}
							<div className="relative">
								<span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-label2 text-trade-text-faint">
									{t("tpsl.sl-price")}
								</span>
								<Input
									type="number"
									step="any"
									min="0"
									value={slPrice}
									onChange={(e) => handleSlPriceChange(e.target.value)}
									placeholder="0.00"
									className="h-10 pl-[72px] text-right text-body2"
								/>
							</div>
							{/* Loss % */}
							<div className="relative">
								<span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-label2 text-trade-text-faint">
									{t("tpsl.loss")}
								</span>
								<Input
									type="number"
									step="any"
									value={slLoss}
									onChange={(e) => handleSlLossChange(e.target.value)}
									placeholder="0"
									className="h-10 pr-8 pl-12 text-right text-body2"
								/>
								<span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-label2 text-trade-text-faint">
									%
								</span>
							</div>

							{/* Expected loss at SL trigger */}
							{toNumber(slPrice) > 0 && effectiveSize > 0 && (
								<div className="col-span-2 flex justify-between px-1 text-label2">
									<span className="text-trade-text-faint">
										{t("tpsl.expected-loss")}
									</span>
									<span
										className={
											slPnl >= 0 ? "text-trade-green" : "text-trade-red"
										}
									>
										{formatSignedUsd(slPnl)}
										<KrwSubtext usd={slPnl} inheritColor parens inline />
									</span>
								</div>
							)}
						</div>

						{/* Checkboxes */}
						<div className="mt-4 space-y-2.5">
							<div className="flex items-center gap-2 text-body2 text-trade-text-secondary">
								<Checkbox
									id="tpsl-configure-amount"
									checked={configureAmount}
									onCheckedChange={(value) =>
										setConfigureAmount(value === true)
									}
								/>
								<label
									htmlFor="tpsl-configure-amount"
									className="cursor-pointer"
								>
									{t("tpsl.configure-amount")}
								</label>
							</div>

							{configureAmount && (
								<div className="ml-6">
									<Input
										type="number"
										step="any"
										min="0"
										value={customSize}
										onChange={(e) => setCustomSize(e.target.value)}
										placeholder={absSize}
										className="h-9 text-body2"
									/>
								</div>
							)}

							<div className="flex items-center gap-2 text-body2 text-trade-text-secondary">
								<Checkbox
									id="tpsl-limit-price"
									checked={useLimitPrice}
									onCheckedChange={(value) => setUseLimitPrice(value === true)}
								/>
								<label htmlFor="tpsl-limit-price" className="cursor-pointer">
									{t("tpsl.limit-price")}
								</label>
							</div>
						</div>

						{/* Confirm Button */}
						<Button
							type="submit"
							disabled={!tpPrice.trim() && !slPrice.trim()}
							className="mt-5 h-11 w-full rounded-xl bg-primary text-[var(--color-theme-static-white)] hover:bg-primary/80"
						>
							{t("actions.confirm")}
						</Button>
					</form>

					{/* Info Text */}
					<div className="mt-4 rounded-lg border border-trade-border p-3 text-body2 text-trade-text-faint">
						<p className="whitespace-pre-line">{t("tpsl.help")}</p>
					</div>
				</TradeDialogContent>
			</Dialog>
		</div>
	);
}
